import { Box, Popover } from "@mui/material";
import {
  KeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  FILTER_CHIP_DEFS,
  FilterChipDef,
  SearchFilters,
  removeFilter,
  chipDisplayLabel,
} from "./filterChipDefs";
import { rankMatches } from "./searchBarMatcher";
import { usePopoverAnchor } from "./PopoverAnchorContext";
import { useV2HeaderNames } from "../../hooks/useV2HeaderNames";
import SearchBarToken from "./SearchBarToken";
import SearchBarSuggestions from "./SearchBarSuggestions";

interface SearchBarProps {
  readonly filters: SearchFilters;
  readonly onFiltersChange: (f: SearchFilters) => void;
  readonly customerId?: string;
  readonly siteMap?: Map<string, string>;
}

type Pending =
  | { mode: "idle"; rawText: string }
  | { mode: "value"; chipDef: FilterChipDef; rawText: string };

export default function SearchBar({
  filters,
  onFiltersChange,
  customerId,
  siteMap,
}: SearchBarProps) {
  const { t } = useTranslation(["search"]);
  const { open: openPopover } = usePopoverAnchor();
  const { getLabel } = useV2HeaderNames(customerId);
  const getDisplayLabel = useCallback(
    (chipDef: FilterChipDef) => chipDisplayLabel(chipDef, getLabel),
    [getLabel],
  );

  const idPrefix = useId();
  const suggestionsListId = `${idPrefix}-suggestions`;

  const [pending, setPending] = useState<Pending>({ mode: "idle", rawText: "" });
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [focused, setFocused] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const tokens = useMemo(
    () => FILTER_CHIP_DEFS.filter((c) => c.hasValue(filters)),
    [filters],
  );

  const matches = useMemo(() => {
    if (pending.mode !== "idle") return [];
    return rankMatches(pending.rawText, FILTER_CHIP_DEFS, getLabel);
  }, [pending, getLabel]);

  const showSuggestions =
    focused && pending.mode === "idle" && pending.rawText !== "";
  const showAllOnFocus =
    focused && pending.mode === "idle" && pending.rawText === "";

  const allChipsByPriority = useMemo(
    () => [...FILTER_CHIP_DEFS].sort((a, b) => a.priority - b.priority),
    [],
  );

  const visibleMatches = showAllOnFocus ? allChipsByPriority : matches;

  const handleSuggestionPick = useCallback(
    (chipDef: FilterChipDef) => {
      // Already-set filter convergence (§5.1): treat as edit-existing-token path.
      if (chipDef.hasValue(filters)) {
        if (chipDef.popoverType === "text") {
          setPending({
            mode: "value",
            chipDef,
            rawText: String(
              filters[chipDef.ownedKeys[0] as keyof SearchFilters] ?? "",
            ),
          });
        } else if (containerRef.current) {
          openPopover(chipDef, containerRef.current);
          setPending({ mode: "idle", rawText: "" });
        }
        return;
      }

      // Fresh pick: open the filter clean — never carry the prefix-search query
      // into the picked filter's input. Users start typing from scratch.
      if (chipDef.popoverType === "text") {
        setPending({ mode: "value", chipDef, rawText: "" });
      } else if (containerRef.current) {
        openPopover(chipDef, containerRef.current);
        setPending({ mode: "idle", rawText: "" });
      }
    },
    [filters, openPopover],
  );

  const handleEditToken = useCallback(
    (chipDef: FilterChipDef) => {
      if (chipDef.popoverType === "text") {
        setPending({
          mode: "value",
          chipDef,
          rawText: String(
            filters[chipDef.ownedKeys[0] as keyof SearchFilters] ?? "",
          ),
        });
        setTimeout(() => inputRef.current?.focus(), 0);
      } else if (containerRef.current) {
        openPopover(chipDef, containerRef.current);
      }
    },
    [filters, openPopover],
  );

  const handleRemoveToken = useCallback(
    (chipDef: FilterChipDef) => {
      onFiltersChange(removeFilter(chipDef, filters));
    },
    [filters, onFiltersChange],
  );

  const commitTextValue = useCallback(() => {
    if (pending.mode !== "value") return;
    const trimmed = pending.rawText.trim();
    if (trimmed === "") {
      setPending({ mode: "idle", rawText: "" });
      return;
    }
    const next = {
      ...filters,
      [pending.chipDef.ownedKeys[0]]: trimmed,
    };
    onFiltersChange(next as SearchFilters);
    setPending({ mode: "idle", rawText: "" });
  }, [pending, filters, onFiltersChange]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (pending.mode === "value") {
        if (e.key === "Enter" || e.key === "Tab") {
          e.preventDefault();
          commitTextValue();
        } else if (e.key === "Escape") {
          e.preventDefault();
          setPending({ mode: "idle", rawText: "" });
        }
        return;
      }

      // idle mode
      if (e.key === "Enter" && visibleMatches.length > 0) {
        e.preventDefault();
        const idx = Math.max(
          0,
          Math.min(highlightedIndex, visibleMatches.length - 1),
        );
        handleSuggestionPick(visibleMatches[idx]);
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((i) =>
          Math.min(i + 1, Math.max(0, visibleMatches.length - 1)),
        );
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setPending({ mode: "idle", rawText: "" });
        return;
      }
      if (
        e.key === "Backspace" &&
        pending.rawText === "" &&
        tokens.length > 0
      ) {
        e.preventDefault();
        handleRemoveToken(tokens[tokens.length - 1]);
        return;
      }
    },
    [
      pending,
      visibleMatches,
      highlightedIndex,
      handleSuggestionPick,
      tokens,
      handleRemoveToken,
      commitTextValue,
    ],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (pending.mode === "value") {
      setPending({ ...pending, rawText: e.target.value });
    } else {
      setPending({ mode: "idle", rawText: e.target.value });
    }
  };

  // Reset highlight whenever the query changes in idle mode, or on mode transitions.
  const idleQuery = pending.mode === "idle" ? pending.rawText : null;
  useEffect(() => {
    setHighlightedIndex(0);
  }, [idleQuery]);

  const placeholder =
    pending.mode === "value"
      ? t("search:searchBar.enterField", {
          field: getDisplayLabel(pending.chipDef).toLowerCase(),
          defaultValue: `Enter ${getDisplayLabel(pending.chipDef).toLowerCase()}…`,
        })
      : t(
          "search:searchBar.placeholder",
          "Search by agent, phone, interaction ID…",
        );

  const showDropdown = showSuggestions || showAllOnFocus;

  return (
    <Box
      ref={containerRef}
      onClick={() => {
        setFocused(true);
        setTimeout(() => inputRef.current?.focus(), 0);
      }}
      sx={{
        position: "relative",
        // Bar is content-sized between 320 and 560. The maxWidth caps the
        // bar's hypothetical size in the parent's flex-wrap algorithm, so
        // siblings (saved-search, right cluster) always fit on row 1. Tokens
        // whose total width exceeds 560 wrap onto new lines inside the bar
        // via the inner flexWrap below — bar grows vertically, not pushing
        // siblings off the row.
        flex: "0 1 auto",
        minWidth: 320,
        maxWidth: 560,
        cursor: "text",
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        flexWrap: "wrap",
        border: "1px solid var(--border-subtle)",
        borderRadius: "8px",
        backgroundColor: "var(--bg-raised)",
        px: 1,
        py: 0.5,
      }}
    >
      {/* Magnifying glass icon */}
      <Box
        component="svg"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        sx={{
          width: 14,
          height: 14,
          color: "var(--text-tertiary)",
          flexShrink: 0,
          pointerEvents: "none",
        }}
      >
        <circle cx="11" cy="11" r="8" />
        <path d="M21 21l-4.35-4.35" />
      </Box>

      {/* Active filter tokens */}
      {tokens.map((chipDef) => (
        <SearchBarToken
          key={chipDef.id}
          chipDef={chipDef}
          filters={filters}
          customerId={customerId}
          siteMap={siteMap}
          onEdit={handleEditToken}
          onRemove={handleRemoveToken}
        />
      ))}

      {/* In-progress value-mode prefix pill */}
      {pending.mode === "value" && (
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            px: "8px",
            py: "3px",
            fontSize: "0.75rem",
            color: "var(--accent)",
            fontWeight: 600,
            border: "1px dashed var(--accent)",
            borderRadius: "8px",
            flexShrink: 0,
          }}
        >
          {getDisplayLabel(pending.chipDef)}:
        </Box>
      )}

      <Box
        ref={inputRef}
        component="input"
        type="text"
        // Suppress browser autofill / password-manager popups. The bar isn't
        // a credential or form field — we don't want 1Password/LastPass/etc.
        // to offer logins or saved values here.
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        name="searchbar-q"
        data-1p-ignore="true"
        data-lpignore="true"
        data-form-type="other"
        role="combobox"
        aria-autocomplete="list"
        aria-haspopup="listbox"
        aria-controls={suggestionsListId}
        aria-expanded={showDropdown}
        aria-activedescendant={
          visibleMatches[highlightedIndex]?.id
            ? `${idPrefix}-suggestion-${visibleMatches[highlightedIndex].id}`
            : undefined
        }
        tabIndex={0}
        value={pending.rawText}
        onChange={handleInputChange}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          if (pending.mode === "idle" && pending.rawText === "") {
            setFocused(false);
          }
        }}
        onKeyDown={handleKeyDown}
        placeholder={
          tokens.length === 0 && pending.mode === "idle" ? placeholder : ""
        }
        sx={{
          flex: "1 1 auto",
          minWidth: 80,
          fontSize: "0.8125rem",
          fontFamily: "inherit",
          color: "var(--text-primary)",
          backgroundColor: "transparent",
          border: "none",
          outline: "none",
          padding: 0,
          overflow: "hidden",
          "&::placeholder": { color: "var(--text-muted)" },
        }}
      />

      {/* Suggestions dropdown */}
      {showDropdown && (
        <Popover
          id={suggestionsListId}
          open
          anchorEl={containerRef.current}
          onClose={() => setFocused(false)}
          disableAutoFocus
          disableEnforceFocus
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{
            paper: {
              // Mousedowns inside the paper (scrollbar drag, padding clicks)
              // would otherwise blur the input and unmount the popover
              // mid-interaction. Cancelling the default keeps focus on the
              // input — same trick the option rows use.
              onMouseDown: (e) => e.preventDefault(),
              sx: {
                backgroundColor: "var(--bg-raised)",
                border: "1px solid var(--border-mid)",
                borderRadius: "8px",
                mt: "4px",
                width: 320,
              },
            },
          }}
        >
          <SearchBarSuggestions
            matches={visibleMatches}
            highlightedIndex={highlightedIndex}
            customerId={customerId}
            onPick={handleSuggestionPick}
            idPrefix={idPrefix}
          />
        </Popover>
      )}
    </Box>
  );
}
